import {
    sevenDaysMilli,
    thirtyDaysMilli,
    ninetyDaysMilli
} from '../constants-types/constants';
import {
    ExternalAssessmentStates,
    SurveyUserTypes,
    Impression as ImpressionEnum
} from '../constants-types/Enums';

export enum FilterCategory {
    PeopleAndOrJobs,
    PersonType,
    Administrator,
    MostRecentAssessment,
    ModifiedBy,
    ModifiedDate,
    Impression,
    AssessmentsCompleted,
    Other
}

export enum FilterIndexContext {
    PeopleAndJobs = 1,
    JobMatch,
    Assessments,
    Users
}

export type XFilterType = {
    id: string;
    getDisplayString(params?: any): string;
    getODataQuery(filterContext?: FilterContext, params?: any): string;
    category?: FilterCategory;
    canCoexist?: (filter1: FilterInfo, filter2: FilterInfo) => boolean;
};

/*
Instead of building a highly flexibly filter system as my heart truly desired, I have instead
focussed on meeting my immediate needs. As such, I have built the following assumptions into
the code:
1) All filters with the same FilterType will be grouped into submenus.
2) All filters with the same FilterType will be ORed together. All other filters will be
    ANDed together
*/

// the id is needed because you group based on the type of filter, may be able to remove later
// Right now the tags need to match the name of the filter - fix they programmatically
// Make these all immutable

const peopleAndJobs = 'peopleAndJobsSearchResult';
const assessmentInfo = `${peopleAndJobs}/assessmentInformation`;
const behavioral = `${assessmentInfo}/behavioralAssessmentInformation`;
const cognitive = `${assessmentInfo}/cognitiveInformation`;
const behavioralState = `${behavioral}/assessmentState`;
const cognitiveState = `${cognitive}/assessmentState`;
const behavioralAdmin = `${behavioral}/administeredById`;
const cognitiveAdmin = `${cognitive}/administeredById`;
const completeDate = `${assessmentInfo}/latestAssessmentCompletedDate`;
const behavioralCompletedTime = `${behavioral}/completedDateTime`;
const cognitiveCompletedTime = `${cognitive}/assessmentCompletedDateTime`;
const sentDate = `${assessmentInfo}/latestAssessmentEmailSentDate`;
const isAPerson = `${peopleAndJobs}/isPerson eq true`;
const isAJob = `${peopleAndJobs}/isPerson eq false`;
const lastModified = `${peopleAndJobs}/sortDate`;
const jobTargetRangesTest = `${peopleAndJobs}/jobInformation/patternScoringInformationInSigma/positionASigmaRangeHigh`;
// We have to query on a specific field even if the entirety of jobInformation is nulled out
// This is because on the actual backend these values get flattened out
const personWithoutJob = `${peopleAndJobs}/jobInformation/id eq null`;

const behavioralCompleted = `${behavioralState} eq ${ExternalAssessmentStates.Completed}`;
const behavioralPending = `${behavioralState} lt ${ExternalAssessmentStates.Completed}`;
const noBehavioral = `${behavioralState} eq null`;
const cognitiveCompleted = `${cognitiveState} eq ${ExternalAssessmentStates.Completed}`;
const cognitivePending = `${cognitiveState} lt ${ExternalAssessmentStates.Completed}`;
const noCognitive = `${cognitiveState} eq null`;

const jobAssessmentUser = 'jobAssociatedPeople/person/assessmentUser';
const jobAssessmentInformation =
    'jobAssociatedPeople/person/assessmentInformation';
const jobBehavioralCompletedTime = `${jobAssessmentUser}/behavioralAssessmentInformation/completedDateTime`;
const jobCognitiveCompletedTime = `${jobAssessmentUser}/cognitiveInformation/assessmentCompletedDateTime`;

export const FilterType: {
    [key: string]: XFilterType;
} = {
    Search: {
        id: 'Search',
        getDisplayString: () => 'filter.search',
        getODataQuery: (filterContext: FilterContext, params: any) => {
            const searchTerm: string = params.term;
            let userODataQuery: string = FilterInfo.orPredicates([
                FilterInfo.predicateBuilder('email', searchTerm),
                FilterInfo.predicateBuilder('firstname', searchTerm),
                FilterInfo.predicateBuilder('lastname', searchTerm)
            ]);
            if (filterContext.indexContext === FilterIndexContext.Users) {
                // If search term is email search only email
                if (searchTerm.indexOf('@') > -1) {
                    userODataQuery = FilterInfo.predicateBuilder(
                        'email',
                        searchTerm
                    );
                    // If search string is a full name : 'John Doe' is split into 'John' and 'Doe'.
                } else if (searchTerm.split(' ').length > 1) {
                    // Capping the number of search terms to two.
                    const searchWords = searchTerm.split(' ', 2);

                    userODataQuery = FilterInfo.orPredicates([
                        FilterInfo.predicateBuilder(
                            'firstname',
                            searchWords[0]
                        ),
                        FilterInfo.predicateBuilder('lastname', searchWords[0]),
                        FilterInfo.predicateBuilder(
                            'firstname',
                            searchWords[1]
                        ),
                        FilterInfo.predicateBuilder('lastname', searchWords[1])
                    ]);
                }
            }

            // User Search makes use of both the search term and the isAccessEnabled setting in a single query
            if (params.isAccessEnabled) {
                // If there are no search terms, optimize the query to just check isEnabled
                if (params.term !== '') {
                    userODataQuery =
                        `isEnabled eq true and (` + userODataQuery + `)`;
                } else {
                    userODataQuery = `isEnabled eq true`;
                }
            }
            return userODataQuery;
        },
        category: FilterCategory.Other
    },
    FindPersonById: {
        id: 'FindPeopleById',
        getDisplayString: () => '',
        getODataQuery: (
            _filterContext: FilterContext,
            params: { personId: string }
        ) =>
            `peopleAndJobsSearchResult/assessmentUser/assessmentUserId eq '${params.personId}'`,
        canCoexist: (filter1, filter2) => {
            if (
                filter1.type === FilterType.FindPersonById &&
                filter2.type === FilterType.FindPersonById
            ) {
                return true;
            } else {
                return filter1.type !== filter2.type;
            }
        }
    },
    FindPersonByEmail: {
        id: 'FindPeopleByEmail',
        getDisplayString: () => '',
        getODataQuery: (
            _filterContext: FilterContext,
            params: { emailAddress: string }
        ) =>
            `peopleAndJobsSearchResult/assessmentUser/email eq '${params.emailAddress}'`,
        canCoexist: (filter1, filter2) => {
            if (
                filter1.type === FilterType.FindPersonByEmail &&
                filter2.type === FilterType.FindPersonByEmail
            ) {
                return true;
            } else {
                return filter1.type !== filter2.type;
            }
        }
    },
    SomeAssessmentsPending: {
        id: 'SomeAssessmentsPending',
        getDisplayString: () => 'filter.someAssessmentsPending',
        getODataQuery: (filterContext: FilterContext) => {
            const cognitiveAvailabilityString = filterContext.showCognitive
                ? `${cognitivePending} or `
                : '';
            return (
                `(${cognitiveAvailabilityString}${behavioralPending})` +
                ` and (${sentDate} ne null)`
            );
        },
        category: FilterCategory.Other
    },
    AllAssessmentsPending: {
        id: 'AllAssessmentsPending',
        getDisplayString: () => 'filter.allAssessmentsPending',
        getODataQuery: (filterContext: FilterContext) => {
            const cognitiveAnyString = filterContext.showCognitive
                ? ` and (${noCognitive} or ${cognitivePending})`
                : '';
            const cognitiveCompletionString = filterContext.showCognitive
                ? ` or ${cognitivePending}`
                : '';
            return (
                `(${noBehavioral} or ${behavioralPending})` +
                `${cognitiveAnyString}` +
                ` and (${behavioralPending}${cognitiveCompletionString})` +
                ` and (${sentDate} ne null)`
            );
        },
        category: FilterCategory.Other
    },
    SomeAssessmentsCompleted: {
        id: 'SomeAssessmentsCompleted',
        getDisplayString: () => 'filter.someAssessmentsCompleted',
        getODataQuery: (filterContext: FilterContext) => {
            const cognitiveAvailabilityString = filterContext.showCognitive
                ? `${cognitiveCompleted} or `
                : '';
            return (
                `(${cognitiveAvailabilityString}${behavioralCompleted})` +
                ` and (${completeDate} ne null)`
            );
        },
        category: FilterCategory.Other
    },
    Folder: {
        id: 'Folder',
        getDisplayString: () => 'filter.folder',
        getODataQuery: (_filterContext: FilterContext, params: any) =>
            `peopleAndJobsSearchResult/folderId eq '${params.folderId}'`,
        category: FilterCategory.Other
    },
    PeopleAdministeredByMe: {
        id: 'PeopleAdministeredByMe',
        getDisplayString: () => 'filter.administeredByMe',
        getODataQuery: (filterContext: FilterContext) => {
            const cognitiveAvailabilityString = filterContext.showCognitive
                ? ` or (${cognitiveAdmin} eq '${filterContext.userGuid}')`
                : '';
            return `(${behavioralAdmin} eq '${filterContext.userGuid}')${cognitiveAvailabilityString}`;
        },
        category: FilterCategory.Administrator
    },
    AllBehavioralAssessmentsForUser: {
        id: 'AllBehavioralAssessmentsForUser',
        getDisplayString: () => 'filter.allBehavioralAssessmentsForUser',
        getODataQuery: (filterContext: FilterContext, params: any) => {
            if (filterContext.indexContext === FilterIndexContext.Assessments) {
                return `assessmentUser/assessmentUserId eq '${params.userId}'`;
            }
        },
        category: FilterCategory.Other
    },
    AllCognitiveAssessmentsForUser: {
        id: 'AllCognitiveAssessmentsForUser',
        getDisplayString: () => 'filter.allBehavioralAssessmentsForUser',
        getODataQuery: (filterContext: FilterContext, params: any) => {
            if (filterContext.indexContext === FilterIndexContext.Assessments) {
                return `assessmentUser/assessmentUserId eq '${params.userId}'`;
            }
        },
        category: FilterCategory.Other
    },
    JobModifiedByMe: {
        id: 'JobModifiedByMe',
        getDisplayString: () => 'filter.jobModifiedByMe',
        getODataQuery: (filterContext: FilterContext) =>
            `peopleAndJobsSearchResult/jobInformation/modifiedById eq '${filterContext.userGuid}'`,
        category: FilterCategory.ModifiedBy
    },
    Jobs: {
        id: 'Jobs',
        getDisplayString: () => 'filter.jobs',
        getODataQuery: () => `${isAJob}`,
        category: FilterCategory.PeopleAndOrJobs
    },
    Group: {
        id: 'Group',
        getDisplayString: () => 'filter.team',
        getODataQuery: () => 's',
        category: FilterCategory.Other
    },
    LastSevenDaysAssessment: {
        id: 'LastSevenDaysAssessment',
        getDisplayString: () => 'filter.lastSevenDays',
        getODataQuery: () =>
            `${completeDate} gt ${new Date(
                Date.now() - sevenDaysMilli
            ).toISOString()}`,
        category: FilterCategory.MostRecentAssessment
    },
    LastThirtyDaysAssessment: {
        id: 'LastThirtyDaysAssessment',
        getDisplayString: () => 'filter.lastThirtyDays',
        getODataQuery: () =>
            `${completeDate} gt ${new Date(
                Date.now() - thirtyDaysMilli
            ).toISOString()}`,
        category: FilterCategory.MostRecentAssessment
    },
    LastNinetyDaysAssessment: {
        id: 'LastNinetyDaysAssessment',
        getDisplayString: () => 'filter.lastNinetyDays',
        getODataQuery: () =>
            `${completeDate} gt ${new Date(
                Date.now() - ninetyDaysMilli
            ).toISOString()}`,
        category: FilterCategory.MostRecentAssessment
    },
    TypeCandidate: {
        id: 'TypeCandidate',
        getDisplayString: () => 'filter.type-candidate',
        getODataQuery: (filterContext: FilterContext) => {
            if (
                filterContext.indexContext === FilterIndexContext.PeopleAndJobs
            ) {
                return `(${isAPerson}) and (peopleAndJobsSearchResult/assessmentUser/personType eq ${SurveyUserTypes.Candidate})`;
            }
            if (filterContext.indexContext === FilterIndexContext.JobMatch) {
                return `${jobAssessmentUser}/personType eq ${SurveyUserTypes.Candidate}`;
            }
        },
        category: FilterCategory.PersonType
    },
    TypeEmployee: {
        id: 'TypeEmployee',
        getDisplayString: () => 'filter.type-employee',
        getODataQuery: () =>
            `(${isAPerson}) and (peopleAndJobsSearchResult/assessmentUser/personType eq ${SurveyUserTypes.Employee})`,
        category: FilterCategory.PersonType
    },
    TypeUnknown: {
        id: 'TypeUnknown',
        getDisplayString: () => 'filter.type-unknown',
        getODataQuery: () =>
            `(${isAPerson}) and (peopleAndJobsSearchResult/assessmentUser/personType eq ${SurveyUserTypes.Unknown})`,
        category: FilterCategory.PersonType
    },
    ImpressionFavorite: {
        id: 'ImpressionFavorite',
        getDisplayString: () => 'filter.impression-favorite',
        getODataQuery: (filterContext: FilterContext) => {
            if (
                filterContext.indexContext === FilterIndexContext.PeopleAndJobs
            ) {
                return `(peopleAndJobsSearchResult/isPerson eq true) and (peopleAndJobsSearchResult/assessmentUser/impression eq ${ImpressionEnum.Favorite})`;
            }
            if (filterContext.indexContext === FilterIndexContext.JobMatch) {
                return `${jobAssessmentUser}/impression eq ${ImpressionEnum.Favorite}`;
            }
        },
        category: FilterCategory.Impression
    },
    People: {
        id: 'People',
        getDisplayString: () => 'filter.people',
        getODataQuery: () => `${isAPerson}`,
        category: FilterCategory.PeopleAndOrJobs
    },
    LastSevenDaysModified: {
        id: 'LastSevenDaysModified',
        getDisplayString: () => 'filter.lastSevenDaysModified',
        getODataQuery: () =>
            `${lastModified} gt ${new Date(
                Date.now() - sevenDaysMilli
            ).toISOString()}`,
        category: FilterCategory.ModifiedDate
    },
    LastThirtyDaysModified: {
        id: 'LastThirtyDaysModified',
        getDisplayString: () => 'filter.lastThirtyDaysModified',
        getODataQuery: () =>
            `${lastModified} gt ${new Date(
                Date.now() - thirtyDaysMilli
            ).toISOString()}`,
        category: FilterCategory.ModifiedDate
    },
    LastNinetyDaysModified: {
        id: 'LastNinetyDaysModified',
        getDisplayString: () => 'filter.lastNinetyDaysModified',
        getODataQuery: () =>
            `${lastModified} gt ${new Date(
                Date.now() - ninetyDaysMilli
            ).toISOString()}`,
        category: FilterCategory.ModifiedDate
    },
    HasCompletedBehavioral: {
        id: 'HasCompletedBehavioral',
        getDisplayString: () => 'filter.hasCompletedBehavioral',
        getODataQuery: (filterContext: FilterContext) => {
            if (
                filterContext.indexContext === FilterIndexContext.PeopleAndJobs
            ) {
                return `(${behavioralCompletedTime} ne null)`;
            }
            if (filterContext.indexContext === FilterIndexContext.JobMatch) {
                return `${jobBehavioralCompletedTime} ne null`;
            }
        },
        category: FilterCategory.AssessmentsCompleted
    },
    HasCompletedCognitive: {
        id: 'HasCompletedCognitive',
        getDisplayString: () => 'filter.hasCompletedCognitive',
        getODataQuery: (filterContext: FilterContext) => {
            if (
                filterContext.indexContext === FilterIndexContext.PeopleAndJobs
            ) {
                return `(${cognitiveCompletedTime} ne null)`;
            }
            if (filterContext.indexContext === FilterIndexContext.JobMatch) {
                return `${jobCognitiveCompletedTime} ne null`;
            }
        },
        category: FilterCategory.AssessmentsCompleted
    },
    HasNotCompletedBehavioral: {
        id: 'HasNotCompletedBehavioral',
        getDisplayString: () => 'filter.hasNotCompletedBehavioral',
        getODataQuery: (filterContext: FilterContext) => {
            if (
                filterContext.indexContext === FilterIndexContext.PeopleAndJobs
            ) {
                return `(${behavioralCompletedTime} eq null)`;
            }
            if (filterContext.indexContext === FilterIndexContext.JobMatch) {
                return `${jobBehavioralCompletedTime} eq null`;
            }
        },
        category: FilterCategory.AssessmentsCompleted
    },
    HasJobTargets: {
        id: 'HasJobTargets',
        getDisplayString: () => 's',
        getODataQuery: () => `(${jobTargetRangesTest} ne null)`,
        category: FilterCategory.Other
    },
    IsDeleted: {
        id: 'IsDeleted',
        getDisplayString: () => 's',
        getODataQuery: () => `IsDeleted eq true`,
        category: FilterCategory.Other
    },
    IsNotDeleted: {
        id: 'IsNotDeleted',
        getDisplayString: () => 's',
        getODataQuery: () => `IsDeleted eq false`,
        category: FilterCategory.Other
    },
    ReferenceProfile: {
        id: 'ReferenceProfile',
        getDisplayString: (params: any) => {
            if (params && params.key) {
                return params.key;
            }
            return 'common.referenceProfile';
        },
        getODataQuery: (filterContext: FilterContext, params: any) => {
            if (
                filterContext.indexContext === FilterIndexContext.PeopleAndJobs
            ) {
                return `(${behavioral}/originalReferencePatternNumber eq ${params.profile})`;
            }
            if (filterContext.indexContext === FilterIndexContext.JobMatch) {
                return `(${jobAssessmentInformation}/behavioralAssessmentInformation/originalReferencePatternNumber eq ${params.profile})`;
            }
        },
        category: FilterCategory.Other,
        canCoexist: (filter1: FilterInfo, filter2: FilterInfo) =>
            filter1.type !== filter2.type ||
            filter1.parameters.profile !== filter2.parameters.profile
    },
    OiUrlsForJob: {
        id: 'OiUrlsForJob',
        getDisplayString: () => 's',
        getODataQuery: (_filterContext: FilterContext, params: any) =>
            `jobId eq '${params.jobId}'`,
        category: FilterCategory.Other
    },
    OiUrlsForLinkName: {
        id: 'OiUrlsForLinkName',
        getDisplayString: () => 's',
        getODataQuery: (_filterContext: FilterContext, params: any) =>
            `linkName eq '${params.linkName}'`,
        category: FilterCategory.Other
    },
    HasEmail: {
        id: 'Folder',
        getDisplayString: () => 's',
        getODataQuery: (_filterContext: FilterContext) =>
            `peopleAndJobsSearchResult/assessmentUser/email ne ''`,
        category: FilterCategory.Other
    },
    PeopleWithoutJobs: {
        id: 'PeopleWithoutJobs',
        getDisplayString: () => '',
        getODataQuery: (_filterContext: FilterContext) => personWithoutJob
    },
    isAccessEnabled: {
        id: 'IsAccessEnabled',
        getDisplayString: () => 's',
        getODataQuery: () => `isEnabled eq true`,
        category: FilterCategory.Other
    },
    NotInEmail: {
        id: 'NotInEmail',
        getDisplayString: () => '',
        getODataQuery: (_filterContext: FilterContext, params: any) =>
            params.length == 0
                ? ''
                : "not (email in ('" + params.join("', '") + "'))"
    }
};

export enum FilterGrouping {
    Category = 1,
    SubMenu
}

type FilterContext = {
    indexContext: FilterIndexContext;
    showCognitive: boolean;
    userGuid: string;
};

// TODO - this could be a real object with a GetDisplayString to ease some long lines
export class FilterInfo {
    type: XFilterType;
    parameters?: any;
    showFilter?: () => boolean;

    // UI property
    managesOwnClick: boolean;

    constructor(obj: Partial<FilterInfo>) {
        Object.assign(this, obj);
    }
    getDisplayString() {
        return this.type.getDisplayString(this.parameters);
    }
    getODataQuery(filterContext: FilterContext) {
        return this.type.getODataQuery(filterContext, this.parameters);
    }
    canCoexist(otherFilter: FilterInfo) {
        if (this.type.canCoexist) {
            return this.type.canCoexist(this, otherFilter);
        }
        return this.type !== otherFilter.type;
    }

    static predicateBuilder(searchKey: string, searchTerm: string) {
        return `contains(${searchKey},'${searchTerm}') `;
    }

    static orPredicates(predicateSnippets: string[]) {
        return predicateSnippets.join(' or ');
    }
}

export const categoryEnumToNameKeyMap: {
    [key: number]: string;
} = {
    [FilterCategory.PeopleAndOrJobs]: 'filterCategory.peopleAndOrJobs',
    [FilterCategory.PersonType]: 'filterCategory.personType',
    [FilterCategory.Administrator]: 'filterCategory.administrator',
    [FilterCategory.MostRecentAssessment]:
        'filterCategory.mostRecentAssessment',
    [FilterCategory.ModifiedBy]: 'filterCategory.modifiedBy',
    [FilterCategory.ModifiedDate]: 'filterCategory.modifiedDate',
    [FilterCategory.Impression]: 'filterCategory.impression',
    [FilterCategory.AssessmentsCompleted]:
        'filterCategory.assessmentsCompleted',
    [FilterCategory.Other]: 'common.Other'
};
